import React, { Fragment, useEffect, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from '@headlessui/react'
import { SizeVariants } from '@/shared/forms/components/constants'
import clsx from 'clsx'
import { GooglePlacesAutocompleteResult } from '@/lib/apis/types'
import {
  useGoogleServices,
  usePlacesAutocompleteService,
  usePlacesDetailsService,
} from '@/shared/hooks/GoogleServiceQueries'
import Spinner from '@/shared/components/Spinner'
import { DetailedAddress } from '@/lib/types'
import { Controller, useFormContext } from 'react-hook-form'
import { RHFTextInputProps } from '@/shared/forms/RHFTextInput'

type Props = {
  name: string
  label: string
  size?: keyof typeof SizeVariants
  autoComplete?: string
  disabled?: boolean
  placeholder?: string
  onPickAddress: (address: DetailedAddress) => void
} & RHFTextInputProps

export default function RHFGooglePlacesAutocompleteInput({
  name,
  label,
  size = 'full',
  autoComplete,
  disabled,
  placeholder,

  optional = false,
  onPickAddress,
}: Props) {
  const { formState, control } = useFormContext()
  const { isSubmitting, touchedFields, errors } = formState
  const isTouched = touchedFields?.[name]
  const error = errors[name]

  const services = useGoogleServices()

  const [input, setInput] = useState('')
  const [selectedPlace, setSelectedPlace] =
    useState<GooglePlacesAutocompleteResult>()
  const debouncedInput = useDebounce(input, 200)
  const predictions = usePlacesAutocompleteService(services, debouncedInput)

  const placeDetailsQuery = usePlacesDetailsService(
    services,
    selectedPlace ? selectedPlace.placeId : '',
  )

  useEffect(() => {
    if (
      !placeDetailsQuery.isLoading &&
      selectedPlace &&
      placeDetailsQuery.data
    ) {
      const data = placeDetailsQuery.data
      const suffix = `, ${data.suburb} ${data.state}`
      const formattedAddress = selectedPlace.description.replace(suffix, '')

      onPickAddress({ formattedAddress, ...placeDetailsQuery.data })
    }
  }, [placeDetailsQuery.isLoading, placeDetailsQuery.data])

  const isDisabled = disabled || isSubmitting

  function handlePickAddress(value: GooglePlacesAutocompleteResult | null) {
    if (value) {
      setInput(value.description)
      setSelectedPlace(value)
    }
  }

  let addressesDropdown
  if (predictions.isLoading) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
        <div className="flex w-full justify-center">
          <Spinner />
        </div>
      </div>
    )
  } else if (!predictions.data || predictions.data.length === 0) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
        No address found.
      </div>
    )
  } else {
    addressesDropdown = (
      <>
        {predictions.data?.map((prediction) => (
          <ComboboxOption
            key={prediction.placeId}
            value={prediction}
            className={({ focus }) =>
              `relative cursor-pointer select-none py-1.5 pl-2.5 pr-4 sm:leading-6 ${
                focus ? 'bg-blue-600 text-white' : 'text-gray-900'
              }`
            }
          >
            {prediction.description}
          </ComboboxOption>
        ))}
      </>
    )
  }

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <div className="flex justify-between">
        <label
          htmlFor={name}
          className={clsx(
            'block text-sm font-medium leading-6',
            isDisabled ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          {label}
        </label>
        {optional && (
          <span className="text-sm leading-6 text-gray-500" id="email-optional">
            Optional
          </span>
        )}
      </div>
      <div className="relative mt-2">
        <Combobox onChange={handlePickAddress}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <ComboboxInput
                type="text"
                autoComplete={autoComplete}
                {...field}
                onChange={(e) => {
                  field.onChange(e)
                  setInput(e.target.value)
                }}
                className={clsx(
                  'block h-10 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
                  isTouched && error ? 'ring-red-600' : 'ring-gray-300',
                  isDisabled ? 'bg-slate-300' : '',
                )}
                id={name}
                name={field.name}
                placeholder={placeholder}
              />
            )}
          />
          {debouncedInput.length >= 3 && (
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ComboboxOptions className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {addressesDropdown}
              </ComboboxOptions>
            </Transition>
          )}
        </Combobox>
        {isTouched && error && (
          <div className="mt-2 text-sm font-semibold text-red-600">
            {`${error.message}`}
          </div>
        )}
      </div>
    </div>
  )
}
